<template>
  <tbody>
    <template v-for="item in items">
      <tr :key="item.Id">

        <!-- FIELDS -->
        <td v-for="column in columnMeta.items"
          :key="column.fieldId"
          :class="{
            'has-text-right': hasTextRight(column),
            'date-field': column.dataType === 'date',
            'long-text': column.fieldId === 'internalNote' || column.fieldId === 'invoiceNote'}">

          <!-- MAIL TO -->
          <template v-if="column.actionType === 'mailto'">
            <a :href="'mailto:' + item[column.fieldId]">{{ item[column.fieldId] }}</a>
          </template>

          <!-- FIELD ACTION -->
          <template v-else>
            <span v-on="{ click: () => column.actionType === 'fieldAction' ? fieldAction(column.action, item) : null }" :class="{ 'is-table-link': column.actionType === 'fieldAction'}">
              <span v-if="column.iconClass" :class="column.iconClass"></span>
              <template v-if="column.dataType === 'success-warning-error'">
                <div v-if="item[column.fieldId] === 'Ok'"><i class="fal fa-check success-warning-error-icon success"></i></div>
                <div v-else-if="item[column.fieldId] === 'Warning'"><i class="fal fa-exclamation success-warning-error-icon warning"></i></div>
                <div v-else-if="item[column.fieldId] === 'Error'"><i class="fal fa-xmark success-warning-error-icon error"></i></div>
              </template>
              <template v-else-if="column.dataType === 'bool'">
                <input type="checkbox" class="switch is-rounded has-no-animations" :checked="item[column.fieldId]" disabled style="cursor: default">
                <label class="has-no-animations" disabled style="cursor: default"></label>
              </template>
              <template v-else-if="column.dataType === 'check'">
                <input type="checkbox" :id="column.fieldId" class="is-checkradio" :checked="item[column.fieldId]" disabled style="cursor: default">
                <label :for="column.fieldId" disabled style="cursor: default" />
              </template>
              <template v-else-if="column.dataType === 'progress'">
                <div class="progress-bar-container">
                  <div class="progress-text">{{ getProgressText(column, item)}}</div>
                  <div class="progress-bar-child progress-bar" :class="{ started: progressIsStarted(column, item) }"></div>
                  <div class="progress-bar-child shrinker" :style="{ width: getInvertedProgressWidthPercent(column, item) }"></div>
                </div>
              </template>
              <template v-else-if="column.dataType === 'callback'">
                <div v-html="column.callback(item)" />
              </template>
              <span v-else-if="column.dataType === 'dateTime'">
                {{ item[column.fieldId] | dateTime }}
              </span>
              <span v-else>{{ displayData(column, item[column.fieldId]) }}</span>
            </span>
          </template>

        </td>

        <!-- ROW ACTIONS -->
        <template v-if="actionMeta || showContextMenu">
          <td class="has-text-right" style="white-space: nowrap">
            <template v-if="actionMeta">
              <template v-for="action in actionMeta.items">
                <template v-if="typeof (action.showCondition) !== 'function' || action.showCondition(item)">
                  <button
                    class="button is-small"
                    :class="action.buttonClass"
                    :key="action.action"
                    :disabled="typeof (action.disableCondition) === 'function' && action.disableCondition(item)"
                    @click="rowAction(action, item)">
                    <span class="icon is-small" v-if="action.iconClass">
                      <i :class="action.iconClass"></i>
                    </span>
                    <span v-if="action.label">{{ action.label }}</span>
                  </button>
                </template>
              </template>
            </template>

            <context-menu v-if="showContextMenu" :item="item"></context-menu>

          </td>
        </template>

        <!-- <template v-if="showContextMenu">
          <td class="has-text-right">

          </td>
        </template> -->
      </tr>

      <template v-if="columnMeta.rowProgress">
        <tr class="progress-row" :key="item[0]">
          <td :colspan="getTotalColSpan()">
            <div class="bar-wrapper">
              <div class="bar" :style="{ width: getProgressWidthPercent(item) }" :class="{ completed: isProgressCompleted(item) }"></div>
            </div>
          </td>
        </tr>

        <!-- An extra just to make the stripe table work... -->
        <tr class="progress-row" :key="item[0]"><td :colspan="getTotalColSpan()"></td></tr>
      </template>

    </template>
  </tbody>
</template>

<script>
import ContextMenu from '@/components/nav/ContextMenu'
import { formatNumber } from '@/helpers'

export default {
  components: {
    ContextMenu
  },
  props: {
    items: null,
    columnMeta: Object,
    actionMeta: Object,
    showContextMenu: Boolean
  },
  methods: {
    rowAction (action, item) {
      this.$emit('rowAction', action.action, item)
    },
    fieldAction (action, item) {
      this.$emit('fieldAction', action, item)
    },
    hasTextRight (column) {
      switch (column.dataType) {
        case 'number':
        case 'currency':
          return true
        default:
          return false
      }
    },
    joinArray (column, array) {
      let subItems = JSON.parse(JSON.stringify(array))
      let overflow = false

      if (subItems.length > 3) {
        subItems = subItems.splice(0, 3)
        overflow = true
      }

      let ret = subItems.map(x => x[column.displayProperty]).join(', ')

      if (overflow) {
        ret += '…'
      }

      return ret
    },

    getTotalColSpan () {
      return this.columnMeta.items.length + (this.actionMeta || this.showContextMenu ? 1 : 0)
    },

    progressIsStarted (column, item) {
      const progress = parseInt(item[column.fieldId])
      const total = parseInt(item[column.fieldIdTotal])

      if (progress === 0 || total === 0) {
        return false
      }

      return true
    },

    getInvertedProgressWidthPercent (column, item) {
      const progress = parseInt(item[column.fieldId])
      const total = parseInt(item[column.fieldIdTotal])

      if (total === 0) {
        return '100%'
      }

      return 100 - Math.ceil(progress * 100 / total) + '%'
    },

    getProgressText (column, item) {
      const progress = parseInt(item[column.fieldId])
      const total = parseInt(item[column.fieldIdTotal])

      if (total === 0) {
        return '-'
      }

      return `${Math.ceil(progress * 100 / total)}% (${progress}/${total})`
    },

    getProgressWidthPercent (item) {
      const progress = parseInt(item[this.columnMeta.rowProgress.fieldIdValue])
      const total = parseInt(item[this.columnMeta.rowProgress.fieldIdTotal])

      if (total === 0) {
        return '0%'
      }

      return `${Math.ceil(progress * 100 / total)}%`
    },

    isProgressCompleted (item) {
      const progress = parseInt(item[this.columnMeta.rowProgress.fieldIdValue])
      const total = parseInt(item[this.columnMeta.rowProgress.fieldIdTotal])

      return (progress > 0 && progress === total)
    },

    displayData (column, value) {
      let ret = ''

      if (Array.isArray(value)) {
        if (value.length === 0) {
          ret = '(inga)'
        } else {
          ret = this.joinArray(column, value)
        }
      }

      switch (column.dataType) {
        case 'date':
          if (value) {
            ret = this.$moment(value).format('YYYY-MM-DD')
          }
          else {
            ret = ''
          }
          break

        case 'translate':
          const translation = column.translations.filter(x => x.key === value)
          if (translation && translation[0] && translation[0].value) {
            ret = translation[0].value
          } else {
            ret = value
          }
          break

        case 'number':
          ret = formatNumber(value)
          break

        case 'currency':
          ret = formatNumber(value) + ' kr'
          break

        case 'days':
          if (parseInt(value) > 1) {
            ret = value + ' dagar'
          } else {
            ret = value + ' dag'
          }
          break

        default:
          ret = value
          break
      }

      if (column.suffix) {
        ret += ' ' + column.suffix
      }

      return ret
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/common/common';

.icon {
  font-size: 1.3em;
}
.is-table-link {
  color: black;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.date-field {
  width: 7rem;
  min-width: 7rem;
}

.long-text {
  max-width: 15rem;
}

/* DISABLE ANIMATION FOR CHECKBOXES */
.switch[type=checkbox] + label::after, .switch[type=checkbox] + label:after {
  transition: none;
}

.progress-bar-container {
  width: 100%;
  height: 1.4rem;
  position: relative;
  border-radius: 0.7rem;
  overflow: hidden;
  top: 0.1rem;

  .progress-text {
    position: absolute;
    top: -0.05rem;
    text-align: center;
    width: 100%;
    z-index: 2;
  }

  .progress-bar-child {
    width: 100%;
    height: 100%;

    &.progress-bar {
      text-align: center;
      animation-direction: reverse;

      &.started {
        // background: linear-gradient(to right, #e5405e 0%, #ffdb3a 45%, #3fffa2 100%);
        background-color: $onflow-green;
      }
    }

    &.shrinker {
      background-color: #f2f2f2;
      position: absolute;
      top: 0;
      right: 0;
    }
  }
}

.progress-row {
  overflow: hidden;
  white-space: nowrap;
  border: none;

  td {
    border: none;
    height: 0;
    line-height: 0;
    padding: 0;

    .bar-wrapper {
      width: 100%;
      position: relative;
      background-color: #f2f2f2;
      opacity: 0.5;

      .bar {
        position: absolute;
        height: 6px;
        background-color: $onflow-gray;
        bottom: 1px;

        &.completed {
          background-color: $primary;
          opacity: 0.6;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/table/AgileTableBody.vue